/**
 * Web component: Emarsys Newsletter (emarsys-newsletter)
 * -----------------------------------------------------------------------------
 * Handles Emarsys newsletter subscription with success message display.
 *
 */

/**
 * Create new <emarsys-newsletter> custom element.
 */
class EmarsysNewsletter extends HTMLElement {

  /**
   * Custom element constructor.
   * - Always call super first in constructor.
   */
  constructor() {
    super();

    // Store the 'result' query parameter in a variable
    this.resultParam = this.getQueryParameter('result');
  }

  connectedCallback() {
    this.checkResult();
    this.closeModalButtonHandler();

    // Initialize button state (disabled by default)
    this.toggleButtonState(false);

    setTimeout( () => {
      this.closeModal();
    }, 15000);

    this.querySelector('.js-emarsys-newsletter-input')?.addEventListener('input', this.validateEmailField.bind(this));
    this.querySelector('.js-emarsys-checkbox-optin')?.addEventListener('change', this.validateOptinField.bind(this));
    this.querySelector('.js-emarsys__signup-button')?.addEventListener('click', function(event) {
      event.preventDefault();
      if (this.CheckInputs()) {
        this.SubmitIt(event);
      }
    }.bind(this));
  }

  onbeforesubmit() {
    return true;
  }

  /**
   * Function to toggle button state based on validation
   */
  toggleButtonState(isValid) {
    const button = this.querySelector('.js-emarsys__signup-button');
    if (button) {
      button.disabled = !isValid;
      if (isValid) {
        button.classList.remove('disabled');
      } else {
        button.classList.add('disabled');
      }
    }
  }

  /**
   * Function to check inputs and validate email
   */
  CheckInputs() {
    const newsletterField = this.querySelector('.js-newsletter-field');
    const dataNewsletterEmpty = newsletterField.getAttribute('data-newsletter-empty');
    const dataNewsletterEmailError = newsletterField.getAttribute('data-newsletter-email-error');
    const emailField = this.querySelector('.js-emarsys-newsletter-input');
    const errorMessage = this.querySelector('.js-newsletter-error-message');
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const checkboxField = this.querySelector('.js-checkbox-field');
    const checkboxOptin = this.querySelector('.js-emarsys-checkbox-optin');
    const checkboxErrorMessage = this.querySelector('.js-checkbox-error-message');
    const dataCheckboxError = checkboxField.getAttribute('data-checkbox-error');

    let email = emailField.value;
    let isValid = true;

    // Reset error message
    errorMessage.style.display = 'none';
    errorMessage.innerText = '';
    newsletterField.classList.remove('newsletter_field--error');
    newsletterField.classList.remove('newsletter_field--valid');

    checkboxErrorMessage.style.display = 'none';
    checkboxErrorMessage.innerText = '';
    checkboxField.classList.remove('checkbox_field--error');

    if (email === '') {
      newsletterField.classList.add('newsletter_field--error');
      errorMessage.style.display = 'block';
      errorMessage.innerText = dataNewsletterEmpty;
      isValid = false;
    } else if (!emailPattern.test(email)) {
      newsletterField.classList.add('newsletter_field--error');
      errorMessage.style.display = 'block';
      errorMessage.innerText = dataNewsletterEmailError;
      isValid = false;
    }

    if (checkboxOptin.checked === false) {
      checkboxField.classList.add('checkbox_field--error');
      checkboxErrorMessage.style.display = 'block';
      checkboxErrorMessage.innerText = dataCheckboxError;
      isValid = false;
    }

    // If validation passes, enable the button
    if (isValid) {
      newsletterField.classList.add('newsletter_field--valid');
      this.toggleButtonState(true);
    } else {
      this.toggleButtonState(false);
    }

    return isValid;
  }

  /**
   * Function to validate email field with a delay
   * This is to ensure that the input is not validated too frequently
  */
  validateEmailField() {
    setTimeout(() => {
      this.CheckInputs();
    }, 500);
  }

  validateOptinField() {
    setTimeout(() => {
      this.CheckInputs();
    }, 500);
  }

  /**
   * Function to submit the form
  */
  SubmitIt(){
    if(window.onbeforesubmit) {
      this.onbeforesubmit();
    }
    this.querySelector('form').submit();
  }

  /**
   * Function to get query parameter value from the URL
   * @param {string} param - The name of the query parameter
   */
  getQueryParameter(param) {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    return params.get(param);
  }

  /**
   * Function to create the success content dynamically
   */
  createSuccessContent() {
    const successMessage = this.querySelector('.js-emarsys-newsletter-success');
    if (successMessage) {
      const heading = successMessage.getAttribute('data-success-message-heading');
      const text = successMessage.getAttribute('data-success-message-text');
      const logo = successMessage.getAttribute('data-logo');
      const closeText = successMessage.getAttribute('data-close-text');
      const closeIcon = successMessage.getAttribute('data-close-icon');

      successMessage.innerHTML = `
        <div class="emarsys-newsletter-success__pop-up">
          <h2 id="dialog_label" class="emarsys-newsletter-success__title">${heading}</h2>
          <div id="dialog_desc" class="emarsys-newsletter-success__text">${text}</div>
          <div class="emarsys-newsletter-success__logo" aria-hidden="true">
            <span>${logo}</span>
          </div>
          <button type="button"
            class="emarsys-newsletter-success__close js-emarsys-newsletter-success-close modal__close-button link link--text focus-inset"
            aria-label="${closeText}">
            <span class="svg-wrapper">${closeIcon}</span>
          </button>
        </div>
      `;

      successMessage.classList.add('emarsys-newsletter-success--visible');
      successMessage.setAttribute('aria-hidden', 'false');
      setTimeout( () => {
        successMessage.focus();
      }, 3000)
    }
  }
  /**
   * Check the 'result' query parameter and display success message if applicable.
   */
  checkResult() {
    const successMessage = this.querySelector('.js-emarsys-newsletter-success');

    if (this.resultParam === 'success') {
      successMessage.classList.add('emarsys-newsletter-success--visible');
      this.createSuccessContent();
    }
  }

  /**
   * Function to close the modal and reset the success message
   */
  closeModal() {
    const successMessage = this.querySelector('.js-emarsys-newsletter-success');

    if (successMessage.innerHTML !== '') {
      successMessage.classList.remove('emarsys-newsletter-success--visible');
      successMessage.setAttribute('aria-hidden', 'true');
    }
  }

  /**
   * Function to handle the close button click event
   */
  closeModalButtonHandler() {
    const closeBtn = this.querySelector('.js-emarsys-newsletter-success-close');

    if (closeBtn) {
      closeBtn.addEventListener('click', function() {
        this.closeModal();
      }.bind(this));
    }
  }
}

customElements.define('emarsys-newsletter', EmarsysNewsletter)
